import React, { useState } from 'react';

export interface RatingProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Current rating value */
  value?: number;
  /** Maximum value */
  max?: number;
  /** Whether the rating can be changed by the user */
  readOnly?: boolean;
  /** Whether the rating is disabled */
  disabled?: boolean;
  /** Size of the icons */
  size?: 'sm' | 'md' | 'lg';
  /** Callback when rating changes */
  onChange?: (value: number) => void;
  /** Show value label */
  showValue?: boolean;
  /** The icon to use for filled stars */
  filledIcon?: React.ReactNode;
  /** The icon to use for empty stars */
  emptyIcon?: React.ReactNode;
  /** Allow half star ratings */
  allowHalf?: boolean;
  /** Gap between icons */
  gap?: 'xs' | 'sm' | 'md' | 'lg';
  /** Color of filled stars */
  color?: 'yellow' | 'orange' | 'red' | 'blue' | 'green' | 'purple';
  /** Custom class names */
  className?: string;
}

export const Rating = React.forwardRef<HTMLDivElement, RatingProps>(
  (
    {
      value = 0,
      max = 5,
      readOnly = false,
      disabled = false,
      size = 'md',
      onChange,
      showValue = false,
      filledIcon,
      emptyIcon,
      allowHalf = false,
      gap = 'sm',
      color = 'yellow',
      className = '',
      ...props
    },
    ref
  ) => {
    // State for hover value
    const [hoverValue, setHoverValue] = useState<number | null>(null);
    
    // Size classes
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
    };
    
    // Gap classes
    const gapClasses = {
      xs: 'gap-0.5',
      sm: 'gap-1',
      md: 'gap-2',
      lg: 'gap-3',
    };
    
    // Color classes
    const colorClasses = {
      yellow: 'text-yellow-400',
      orange: 'text-orange-500',
      red: 'text-red-500',
      blue: 'text-blue-500',
      green: 'text-green-500',
      purple: 'text-purple-500',
    };
    
    // Handle mouse enter on star
    const handleMouseEnter = (index: number) => {
      if (readOnly || disabled) return;
      setHoverValue(index);
    };
    
    // Handle mouse leave
    const handleMouseLeave = () => {
      if (readOnly || disabled) return;
      setHoverValue(null);
    };
    
    // Handle click on star
    const handleClick = (index: number) => {
      if (readOnly || disabled) return;
      
      // If clicking on the current value, clear it (like toggling)
      const newValue = value === index ? 0 : index;
      
      if (onChange) {
        onChange(newValue);
      }
    };
    
    // Determine if a star should be filled based on its index
    const isFilled = (index: number) => {
      const displayValue = hoverValue !== null ? hoverValue : value;
      
      if (allowHalf) {
        return index <= displayValue - 0.5;
      }
      
      return index <= displayValue;
    };
    
    // Determine if a star should be half-filled
    const isHalfFilled = (index: number) => {
      if (!allowHalf) return false;
      
      const displayValue = hoverValue !== null ? hoverValue : value;
      const halfIndex = index - 0.5;
      
      return Math.ceil(displayValue) === index && displayValue === halfIndex;
    };
    
    // Default star SVG icons
    const defaultFilledIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
          clipRule="evenodd"
        />
      </svg>
    );
    
    const defaultEmptyIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
        />
      </svg>
    );
    
    // Half-filled star icon
    const halfFilledIcon = (
      <div className="relative">
        <div className="absolute inset-0 overflow-hidden w-1/2">
          {filledIcon || defaultFilledIcon}
        </div>
        <div className="text-gray-300 dark:text-gray-400">
          {emptyIcon || defaultEmptyIcon}
        </div>
      </div>
    );
    
    // Generate stars
    const stars = [];
    for (let i = 1; i <= max; i++) {
      stars.push(
        <div
          key={i}
          className={`
            ${sizeClasses[size]}
            cursor-pointer
            transition-transform duration-150 ease-in-out
            ${!readOnly && !disabled ? 'hover:scale-110' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onMouseEnter={() => handleMouseEnter(i)}
          onClick={() => handleClick(i)}
          role={readOnly ? 'presentation' : 'button'}
          aria-label={readOnly ? undefined : `Rate ${i} of ${max}`}
        >
          {isHalfFilled(i) ? (
            halfFilledIcon
          ) : (
            <div className={isFilled(i) ? colorClasses[color] : 'text-gray-300 dark:text-gray-400'}>
              {isFilled(i) ? filledIcon || defaultFilledIcon : emptyIcon || defaultEmptyIcon}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div
        ref={ref}
        className={`
          inline-flex items-center
          ${gapClasses[gap]}
          ${className}
        `}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {stars}
        
        {showValue && (
          <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            {value} of {max}
          </span>
        )}
      </div>
    );
  }
);

Rating.displayName = 'Rating';

export default Rating;